import React from 'react';
import { useUser } from '@clerk/clerk-react';

interface AuthGuardProps {
  children: React.ReactNode;
}

/**
 * AuthGuard - Single Responsibility: Check if user is authenticated with Clerk
 * 
 * On success: Renders children
 * On failure: Redirects to sign-in page
 * 
 * SPECIAL CASE: If impersonation parameters are present, allow access even if Clerk
 * session is not fully loaded, as the auth middleware will handle impersonation context.
 * 
 * This guard does NOT check organization status - that's OrganizationGuard's job
 */
export default function AuthGuard({ children }: AuthGuardProps) {
  // Check for impersonation parameters FIRST, before calling any Clerk hooks
  const urlParams = new URLSearchParams(window.location.search);
  const impersonateParam = urlParams.get('impersonate');
  const ghostParam = urlParams.get('org');
  const hasImpersonationParams = !!(impersonateParam || ghostParam);
  
  // SPECIAL CASE: If impersonation parameters are present, bypass Clerk entirely
  // The auth middleware will handle the impersonation context switching
  if (hasImpersonationParams) {
    
    // CRITICAL: Bypass Clerk entirely during impersonation
    // This prevents Clerk from redirecting to /sign-in
    return <>{children}</>;
  }
  
  // Only call Clerk hooks if we're NOT in impersonation mode
  const { user, isLoaded } = useUser();
  
  
  // Show loading while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  // If no user, redirect to sign-in
  if (!user) {
    window.location.href = '/sign-in';
    return null;
  }

  // User is authenticated, allow access
  return <>{children}</>;
}
